import {
  ConsentScope,
  FinancialWheel,
  FundingFulfillmentMode,
  FundingKind,
  FundingProfileSchema,
  PartnerCapability,
  PartnerDeliveryStatus,
  PartnerHandoffSchema,
  PartnerIntegrationStatus,
  ReferralChannel,
  ReferralClaimState,
  ReferralDirection,
  ReferralOnboardingState,
  ReferralPayloadSchema,
  ReferralStatus,
  ResourcePlanSchema,
  ResourceSchema,
} from '../data/types';
import { FoodBusinessFinancialIntelligenceCategories } from './enums';

export const PARTNER_EVENT_SPEC_VERSION = '2026-09-01' as const;
export const REFERRAL_CONSENT_VERSION = '2026-09-01';
export const DEFAULT_ATTRIBUTION_WINDOW_DAYS = 90;

export const DEFAULT_CONSENT_SCOPES: ConsentScope[] = [
  'identity',
  'business_profile',
  'qualification',
];

export const CONSENT_SCOPE_LABELS: Record<ConsentScope, string> = {
  identity: 'Name and email',
  business_profile: 'Business name, type, and location',
  qualification: 'Whether you currently generate sales',
  operational_signals: 'Kitchen booking and usage summaries',
  menu: 'Master menu and recipes',
  supplies: 'Ingredient and supplier costs',
  sales: 'POS and channel sales transactions',
};

export const FINANCIAL_WHEEL_OPTIONS: { value: FinancialWheel; label: string }[] = [
  { value: 'accounting', label: 'Airibook accounting AI' },
  { value: 'margin_by_dish', label: 'Margin by Dish' },
  { value: 'inventory', label: 'Inventory Management and Sourcing' },
  { value: 'loyalty', label: 'AiriHub Diner Insights' },
  { value: 'demand', label: 'Airitime demand forecasting' },
];

/** Optional partner handoff URLs from env — never commit production values. */
export const getAiriWheelsSignupUrlFromEnv = (): string =>
  (process.env.REACT_APP_AIRIWHEELS_SIGNUP_URL || '').trim();

/** Optional partner webhook URL from env — never commit production values. */
export const getAiriWheelsWebhookUrlFromEnv = (): string =>
  (process.env.REACT_APP_AIRIWHEELS_WEBHOOK_URL || '').trim();

export const FULFILLMENT_MODE_OPTIONS: { value: FundingFulfillmentMode; label: string }[] = [
  { value: 'api_handoff', label: 'API handoff (queued webhook + deep link fallback)' },
  { value: 'referral_handoff', label: 'Referral intro (consent + deep link)' },
  { value: 'external_trial', label: 'External trial link' },
];

export const AIRIWHEELS_PARTNER_SLUG = 'airiwheels';

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  opted_in: 'Opted in',
  qualified: 'Qualified',
  handed_off: 'Handed off',
  discovery: 'Discovery',
  activated: 'Activated',
  unqualified: 'Unqualified',
  declined: 'Declined',
};

export const REFERRAL_STATUS_BADGE_CLASS: Record<ReferralStatus, string> = {
  opted_in: 'bg-primary-tint text-primary-100',
  qualified: 'bg-primary-tint text-primary-300',
  handed_off: 'bg-[#EFF8FA] text-ink',
  discovery: 'bg-[#FEF3C7] text-[#92400E]',
  activated: 'bg-[#E7F5EC] text-[#0F973D]',
  unqualified: 'bg-[#F5F5F5] text-muted',
  declined: 'bg-[#FEE2E2] text-[#B91C1C]',
};

export const REFERRAL_DIRECTION_LABELS: Record<ReferralDirection, string> = {
  homecook_to_partner: 'HomeCook → AiriWheels',
  partner_to_homecook: 'AiriWheels → HomeCook',
};

export const REFERRAL_CLAIM_STATE_LABELS: Record<ReferralClaimState, string> = {
  unclaimed: 'Unclaimed',
  claim_pending: 'Claim pending',
  claimed: 'Claimed',
  expired: 'Expired',
  revoked: 'Revoked',
};

export const REFERRAL_ONBOARDING_STATE_LABELS: Record<ReferralOnboardingState, string> = {
  not_started: 'Not started',
  referral_received: 'Referral received',
  contacted: 'Contacted',
  onboarding_started: 'Onboarding started',
  facility_information_needed: 'Facility information needed',
  facility_review_pending: 'Facility review pending',
  ready_for_next_step: 'Ready for next step',
  capacity_active: 'Capacity active',
  declined: 'Declined',
  closed: 'Closed',
};

export const PARTNER_DELIVERY_STATE_LABELS: Record<PartnerDeliveryStatus, string> = {
  queued: 'Queued',
  processing: 'Processing',
  accepted: 'Accepted',
  delivering: 'Delivering',
  delivered: 'Delivered',
  failed: 'Failed',
  dead_letter: 'Needs intervention',
  canceled: 'Canceled',
};

export const PARTNER_INTEGRATION_STATE_LABELS: Record<PartnerIntegrationStatus, string> = {
  disconnected: 'Disconnected',
  pending: 'Pending',
  connected: 'Connected',
  revoked: 'Revoked',
  error: 'Error',
};

export const REFERRAL_CHANNEL_LABELS: Record<ReferralChannel, string> = {
  pantry: 'Resource Pantry',
  joint_seminar: 'Joint seminar',
  incubator: 'Incubator',
  owned_digital: 'Owned digital',
  print: 'Print',
};

export const REFERRAL_CHANNEL_PREFIX: Record<ReferralChannel, string> = {
  pantry: 'PAN',
  joint_seminar: 'SEM',
  incubator: 'INC',
  owned_digital: 'DIG',
  print: 'PRT',
};

export const CHANNEL_CODE_LEGEND = 'HC-{PAN|SEM|INC|DIG|PRT}-{partner}-{hex}';

/** Canonical Resource Pantry URL for a joint seminar. Never use REACT_APP_LANDING_URL_*. */
export const buildJointSeminarUrl = (webUrl: string | undefined, resourceId: string, seminarId: string): string => {
  const base = (webUrl || 'http://localhost:3010').replace(/\/$/, '');
  return `${base}/funding/${resourceId}?channel=joint_seminar&seminar=${seminarId}`;
};

export const REFERRAL_STATUSES: ReferralStatus[] = [
  'opted_in',
  'qualified',
  'handed_off',
  'discovery',
  'activated',
  'unqualified',
  'declined',
];

/** Human label for a referral pipeline status. */
export const formatReferralStatus = (status?: ReferralStatus | string): string => {
  if (!status) return '—';
  return REFERRAL_STATUS_LABELS[status as ReferralStatus] || status.replace(/_/g, ' ');
};

/** Tailwind classes for a referral status pill. */
export const referralStatusBadgeClass = (status?: ReferralStatus | string): string => {
  if (!status) return 'bg-[#F5F5F5] text-muted';
  return REFERRAL_STATUS_BADGE_CLASS[status as ReferralStatus] || 'bg-[#F5F5F5] text-muted';
};

/** Human label for any contract state while remaining forward-compatible. */
export const formatContractState = (
  state: string | undefined,
  labels: Record<string, string>
): string => {
  if (!state) return '—';
  return labels[state] || state.replace(/_/g, ' ');
};

export const AIRIWHEELS_CAPABILITIES: PartnerCapability[] = [
  'deep_link',
  'outbound_webhook',
  'inbound_webhook',
];

/**
 * AiriWheels published access pricing (https://airiwheels.ai/pricing): one fixed
 * monthly access fee with a 14-day free trial, metered token overage, and seat limits.
 */
export const AIRIWHEELS_TRIAL_DAYS = 14;

/**
 * The single scaled-down rate plan a funding listing needs, seeded from the partner's
 * public pricing page. Funding offers price an access fee, not a booking slot, so this
 * carries no max duration or inventory associations.
 */
export const AIRIWHEELS_RATE_PLAN: ResourcePlanSchema = {
  plan_name: 'Access Fee',
  plan_description: 'One fixed access fee for everyone. Includes 2.5M tokens and 3 user seats per month.',
  plan_rate: 89,
  plan_recurrence: 'monthly',
  plan_type: 'resource',
  resource_type: 'funding',
  pricing_type: 'fixed',
  // overage_rate: 11,
  features: [
    '2.5M tokens included each month',
    '$11 per additional 1M tokens',
    '3 user seats included, $10 per additional user',
    'Autonomous accounting and daily bank reconciliation',
    'Margin-by-dish analysis',
    'Inventory, smart reordering, and multi-vendor sourcing',
    'Ask Airi, your AI finance partner',
  ],
};

/** Default eligibility notes for the AiriWheels partner listing (editable after seed). */
export const AIRIWHEELS_ELIGIBILITY_NOTES =
  'Commercially active restaurants, food trucks, or caterers with transaction or inventory complexity. Pre-launch founders with no sales are out of scope for now.';

/**
 * Seeded AiriWheels listing a partner can apply and still edit.
 * Signup/webhook come from env when set — never hardcode production partner URLs.
 */
export const AIRIWHEELS_FUNDING_TEMPLATE: Partial<ResourceSchema> = {
  name: 'AiriWheels - AI Financial Brain for Restaurants',
  description:
    'AiriWheels is an AI financial back office for restaurants spanning Airibook accounting, margin by dish, Inventory Management and Sourcing, AiriHub Diner Insights, and Airitime demand forecasting. It is best for commercially active restaurants, food trucks, and caterers.',
  resource_subtype: FoodBusinessFinancialIntelligenceCategories.AI_Financial_Operating_System,
  website: 'https://airiwheels.ai/',
  city: 'Boston',
  state: 'MA',
  zipcode: '02108',
  funding_profile: {
    kind: 'financial_intelligence',
    fulfillment_mode: 'api_handoff',
    wheels: ['accounting', 'margin_by_dish', 'inventory', 'loyalty', 'demand'],
    eligibility: {
      requires_active_sales: true,
      excluded_stages: ['idea'],
      notes: AIRIWHEELS_ELIGIBILITY_NOTES,
    },
    partner: {
      slug: AIRIWHEELS_PARTNER_SLUG,
      display_name: 'AiriWheels',
      signup_url: getAiriWheelsSignupUrlFromEnv() || '',
      webhook_url: getAiriWheelsWebhookUrlFromEnv() || '',
      attribution_window_days: DEFAULT_ATTRIBUTION_WINDOW_DAYS,
      privacy_url: 'https://airiwheels.ai/privacy',
      capabilities: AIRIWHEELS_CAPABILITIES,
    },
    pricing_summary: `$${AIRIWHEELS_RATE_PLAN.plan_rate}/month, ${AIRIWHEELS_TRIAL_DAYS}-day trial`,
    trial_days: AIRIWHEELS_TRIAL_DAYS,
  },
};

type QualificationInput = {
  commercially_active?: boolean
  business_stage?: string
  business_type?: string
  requires_active_sales?: boolean
  excluded_stages?: string[]
}

type QualificationResult = {
  qualified: boolean
  reason: string
}

/** Rule-based ICP check for financial-intelligence listings. O(1). */
export const qualifyFinancialResource = (input: QualificationInput): QualificationResult => {
  const excluded = (input.excluded_stages || []).map((stage) => stage.toLowerCase());
  const stage = (input.business_stage || '').toLowerCase();
  if (input.requires_active_sales !== false && input.commercially_active === false) {
    return {
      qualified: false,
      reason: 'This partner is for commercially active food businesses with current sales.',
    };
  }
  if (stage && excluded.includes(stage)) {
    return {
      qualified: false,
      reason: 'This partner is not a fit at the idea / pre-launch stage yet.',
    };
  }
  return {
    qualified: true,
    reason: 'This business looks like a fit for financial operating intelligence.',
  };
};

export const getFundingKind = (resource?: ResourceSchema | null): FundingKind => {
  return resource?.funding_profile?.kind || 'capital';
};

export const isFinancialIntelligenceResource = (resource?: ResourceSchema | null): boolean => {
  return getFundingKind(resource) === 'financial_intelligence';
};

/** True when the listing is the seeded AiriWheels financial-intelligence partner. */
export const isAiriWheelsResource = (resource?: ResourceSchema | null): boolean =>
  resource?.funding_profile?.partner?.slug === AIRIWHEELS_PARTNER_SLUG;

export const getFundingFulfillmentMode = (resource?: ResourceSchema | null): FundingFulfillmentMode => {
  return resource?.funding_profile?.fulfillment_mode || 'application';
};

export const usesReferralHandoff = (resource?: ResourceSchema | null): boolean => {
  const mode = getFundingFulfillmentMode(resource);
  return isFinancialIntelligenceResource(resource) && (mode === 'referral_handoff' || mode === 'api_handoff' || mode === 'external_trial');
};

export const getPartnerDisplayName = (resource?: ResourceSchema | null): string => {
  return resource?.funding_profile?.partner?.display_name || resource?.name || 'this partner';
};

/**
 * Attach a referral code to the partner signup URL, replacing an existing `ref=` when present.
 */
export const buildPartnerSignupUrl = (signupUrl: string | undefined, referralCode: string): string => {
  const base = signupUrl || 'https://airiwheels.ai/';
  try {
    const url = new URL(base);
    url.searchParams.set('ref', referralCode);
    return url.toString();
  } catch {
    const withoutRef = base.replace(/([?&])ref=[^&]*/g, '').replace(/[?&]$/, '');
    const separator = withoutRef.includes('?') ? '&' : '?';
    return `${withoutRef}${separator}ref=${encodeURIComponent(referralCode)}`;
  }
};

/** Shared partner-handoff shape for non-funding resources. */
export type { PartnerHandoffSchema };

/**
 * Read signup/webhook/fulfillment from funding_profile or partner_handoff.
 * Prefer funding_profile when both exist (AiriWheels FI listing).
 */
export const getResourcePartnerHandoff = (resource?: ResourceSchema | null): {
  fulfillment_mode?: FundingFulfillmentMode
  signup_url?: string
  webhook_url?: string
  partner?: FundingProfileSchema['partner']
} => {
  const funding = resource?.funding_profile;
  if (funding?.kind === 'financial_intelligence' || funding?.partner) {
    return {
      fulfillment_mode: funding.fulfillment_mode,
      signup_url: funding.partner?.signup_url,
      webhook_url: funding.partner?.webhook_url,
      partner: funding.partner,
    };
  }
  const handoff = resource?.partner_handoff;
  return {
    fulfillment_mode: handoff?.fulfillment_mode,
    signup_url: handoff?.partner?.signup_url,
    webhook_url: handoff?.partner?.webhook_url,
    partner: handoff?.partner,
  };
};

export const filterConsentedPayload = (
  payload: ReferralPayloadSchema,
  scope: ConsentScope[] = DEFAULT_CONSENT_SCOPES
): ReferralPayloadSchema => {
  const allowed = new Set(scope);
  const filtered: ReferralPayloadSchema = {};
  if (allowed.has('identity')) {
    filtered.name = payload.name;
    filtered.email = payload.email;
  }
  if (allowed.has('business_profile')) {
    filtered.business_name = payload.business_name;
    filtered.city = payload.city;
    filtered.state = payload.state;
    filtered.business_type = payload.business_type;
  }
  if (allowed.has('qualification')) {
    filtered.commercially_active = payload.commercially_active;
    filtered.reason = payload.reason;
    filtered.next_action = payload.next_action;
  }
  return filtered;
};

const PRICING_RECURRENCE_LABELS: Record<string, string> = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  annual: 'year',
};

/** Format a rate without trailing decimals for whole amounts. */
const formatRate = (rate: number): string => (
  Number.isInteger(rate) ? `$${rate}` : `$${rate.toFixed(2)}`
);

/** Minimal rate-plan shape the pricing summary needs. Any `ResourcePlanSchema` satisfies it. */
export type FundingPricingPlan = Pick<ResourcePlanSchema, 'plan_rate' | 'plan_recurrence'>;

/**
 * Derive the public pricing summary from a funding resource's rate plans.
 * Each recurrence contributes a single entry, and a trial length is appended when present.
 */
export const buildFundingPricingSummary = (
  plans?: FundingPricingPlan[],
  trialDays?: number,
): string => {
  const seen = new Set<string>();
  const segments: string[] = [];
  (plans || []).forEach((plan) => {
    const rate = Number(plan?.plan_rate);
    if (!Number.isFinite(rate) || rate <= 0) return;
    const recurrence = String(plan?.plan_recurrence || '').toLowerCase();
    const unit = PRICING_RECURRENCE_LABELS[recurrence];
    const key = unit || recurrence || 'flat';
    if (seen.has(key)) return;
    seen.add(key);
    segments.push(unit ? `${formatRate(rate)}/${unit}` : formatRate(rate));
  });
  const trial = Number(trialDays);
  if (Number.isFinite(trial) && trial > 0) {
    segments.push(`${trial}-day trial`);
  }
  return segments.join(', ');
};

export const defaultFundingProfile = (kind: FundingKind): FundingProfileSchema => {
  if (kind === 'financial_intelligence') {
    return {
      kind,
      fulfillment_mode: 'api_handoff',
      wheels: [],
      eligibility: { requires_active_sales: true, excluded_stages: ['idea'] },
      partner: { attribution_window_days: DEFAULT_ATTRIBUTION_WINDOW_DAYS, capabilities: ['deep_link'] },
    };
  }
  return { kind: 'capital', fulfillment_mode: 'application' };
};
